<!-- eslint-disable vue/no-v-html -->
<template>
  <div ref="overviewSection" class="overview-media-items">
    <div
      v-for="(item, index) in media"
      :key="`media-${index}`"
      class="overview-media-item-wrapper"
    >
      <div v-if="item.embed_code || item.image" class="media-wrapper">
        <div
          v-if="item.embed_code"
          class="story-inline-video"
          v-html="toggleEmbed ? item.embed_code : (item.lazy_embed_code || '<div />' )"
        />
        <div
          v-else
          class="story-img-container open-carousel-modal-on-click"
          :data-story-image-id="`${item.slug}`"
        >
          <client-only>
            <VLazyImage
              class="story-inline-image"
              :alt="item.alt_text || 'image'"
              :srcset="`${item.image}?width=450 450w,
                ${item.image}?width=595&height=350 595w,
                ${item.image} 850w`"
              :src="item.image"
              :src-placeholder="placeHolderImg"
              sizes="(max-width: 450px) 450px, (max-width: 595px) 595px, 850px"
              width="850"
              height="500"
            />
            <font-awesome-icon
              class="hover-mag-glass"
              icon="search-plus"
            />
          </client-only>
        </div>
        <div v-if="item.description" class="media-description">
          {{ item.description }}
        </div>
      </div>
      <div class="content-wrapper">
        <div
          v-if="item.html_body"
          class="pdp-deal-body pdp-description"
          v-html="item.html_body"
        />
        <div
          v-else
          class="content-container"
          v-html="item.content"
        />
      </div>
      <div v-if="includeShowMore" class="button-container">
        <button
          class="centered"
          @click="showMore"
        >
          Show more details
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import get from 'lodash/get';
import debounce from 'lodash/debounce';
import VLazyImage from 'v-lazy-image';
import { PLACEHOLDER_IMG } from '@/constants/global';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

export default {
  components: {
    FontAwesomeIcon,
    VLazyImage,
  },
  props: {
    media: {
      type: Array,
      default: () => [],
    },
    header: {
      type: String,
      default: '',
    },
    includeShowMore: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      placeholderImg: PLACEHOLDER_IMG,
      toggleEmbed: false,
    };
  },
  mounted() {
    this.handleDebouncedScroll = debounce(this.onScroll, 100);
    window.addEventListener('scroll', this.handleDebouncedScroll);

    const clickableStoryImages = document.querySelectorAll('.open-carousel-modal-on-click');
    clickableStoryImages.forEach((imgElement) => imgElement.addEventListener('click', this.openCarouselModal));
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleDebouncedScroll);

    const clickableStoryImages = document.querySelectorAll('.open-carousel-modal-on-click');
    clickableStoryImages.forEach((imgElement) => imgElement.removeEventListener('click', this.openCarouselModal));
  },
  methods: {
    // On scroll into view, load embed video
    onScroll() {
      if (this.toggleEmbed) return;

      const el = get(this, ['$refs', 'overviewSection']);

      if (el) {
        const rect = el.getBoundingClientRect();
        const isVisible = (
          (rect.top >= 0 && rect.top <= window.innerHeight)
          || (rect.bottom >= 0 && rect.bottom <= window.innerHeight)
        );

        if (isVisible) this.toggleEmbed = true;
      }
    },
    showMore() {
      this.$emit('show-more');
    },
    openCarouselModal(event) {
      if (event.currentTarget?.dataset?.storyImageId) {
        this.$store.dispatch(
          'carouselModal/openModalToImage',
          { targetImageSlug: event.currentTarget.dataset.storyImageId },
        );
      }
    },
  },
};
</script>

<style scoped lang="scss">
  /* TODO -- Remove once this is a global style */
  @import '~/styles/dorado/main';

  .content-container::v-deep {
    font-size: 16px; // default font-size if html tags missing in admin

    h1,
    h2,
    h3,
    h4,
    h5 {
      font-family: $headings-font-family;
      font-weight: $headings-font-weight;
    }

    p,
    a,
    span {
      font-weight: $font-weight-normal;
      font-size: $font-size-base;
      font-family: $font-family-base;
      line-height: 24px;
    }

    b {
      font-weight: $font-weight-bold;
    }
  }

  .media-description {
    color: #434d59;
    font-style: italic;
    font-weight: 400;
    font-size: 14px;
    line-height: 21px;
    padding-top: 8px;
    text-align: center;
    width: 100%;
  }

  img {
    max-width: 100%;
    height: auto;
  }

  .row {
    margin-left: 0;
    margin-right: 0;
    margin-top: 64px;

    &:first-child {
      margin-top: 0;
    }
  }

  .hover-mag-glass,
  .pdp-deal-body ::v-deep .hover-mag-glass {
    font-size: 32px;
    transition: 0.5s ease;
    opacity: 0;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    -ms-transform: translate(-50%, -50%);
    text-align: center;
  }

  .story-inline-image,
  .pdp-deal-body ::v-deep .story-inline-image {
    opacity: 1;
    display: block;
    width: 100%;
    height: auto;
    transition: 0.5s ease;
    backface-visibility: hidden;
    border: 1px solid #c7ced7;
    border-radius: 4px;
    box-sizing: border-box;
    margin: 0 auto;
  }

  .story-img-container {
    position: relative;

    &:hover {
      cursor: pointer;

      .story-inline-image {
        opacity: 0.2;
      }

      .hover-mag-glass {
        opacity: 1;
      }
    }
  }

  .pdp-deal-body::v-deep {
    .story-img-container {
      position: relative;

      &:hover {
        cursor: pointer;

        .story-inline-image {
          opacity: 0.2;
        }

        .hover-mag-glass {
          opacity: 1;
        }
      }
    }

    img {
      border-radius: 4px;
      max-width: 100%;
      height: auto;
      border: 1px solid #c7ced7;
      margin-bottom: 8px;
    }

    b {
      font-weight: $font-weight-bold;
    }

    ul,
    ol {
      margin-bottom: 2.5rem;
    }
  }

  .overview-media-item-wrapper {
    margin-bottom: 3rem;

    &:last-child {
      margin-bottom: 2rem;
    }
  }

  .media-wrapper + .content-wrapper {
    margin-top: 24px;
  }

  /* order not part of bootstrap for some reason */
  .order-1 {
    order: 1;
  }

  .order-2 {
    order: 2;
  }

  @media (min-width: 768px) {
    .order-md-1 {
      order: 1;
    }
  }

  @media (max-width: 767px) {
    .row {
      margin-bottom: 48px;
    }
  }

  // Taken from ProductsOverview
  .button-container {
    margin-top: 36px;

    button {
      border: 2px solid $deep-ocean;
      border-radius: 20px;
      background-color: #fff;
      color: $deep-ocean;
      font-family: $headings-font-family;
      font-weight: $headings-font-weight;
      font-size: 16px;
      transition: background-color 150ms;
      padding: 4px 20px;

      &:focus {
        outline: none;
      }

      &:hover {
        background-color: rgba(14, 110, 244, 0.1);
      }
    }
  }

  @media only screen and (min-width: 767px) {
    .show-mobile {
      display: none;
    }
  }

  @media only screen and (max-width: 767px) {
    .show-lg {
      display: none;
    }
  }

  /**
  * Override legacy styles from `project.css`. This should be removed/updated
  * once dorado is implemented.
  *
  * Using this for now, when `project.css` is removed, should utilize
  *   @supports and aspect-ratio
  */
  .story-inline-video,
  .overview-media-item-wrapper::v-deep .story-inline-video {
    border-radius: 0;
    margin: 0;
    max-width: 100%;
    position: relative;
    padding-bottom: 56.25%;
    height: 0;
    overflow: hidden;

    iframe {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }
  }
</style>
